import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogHeaderComponent, DialogService, StatusBannerComponent } from '@mode/shared/ui';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AutofocusDirective, ButtonComponent } from '@mode/capra';
import {
  AbstractControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { validNameValidator } from '@mode/dataset/ui';
import { MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { CellExportFacade, ContentType } from '@mode/notebooks/contract';
import { DataViewFacade } from '@mode/switch-views/contract';
import { DataViewTypes, ProducerTypes } from '@mode/shared/contract-common';
import { Observable } from 'rxjs';
import { ToastService } from '@mode/shared/util-toast';
import { ExportDatasetLimitExceedComponent } from '@mode/notebooks/util';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { MetaTags, MetaTagsService } from '@mode/shared/util-js';

const duplicateValidator =
  (keys: string[]): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null =>
    keys.includes(control.value?.trim()) ? { invalidNameKey: true } : null;

@Component({
  standalone: true,
  imports: [
    AutofocusDirective,
    CommonModule,
    DialogHeaderComponent,
    StatusBannerComponent,
    DragDropModule,
    ButtonComponent,
    ReactiveFormsModule,
  ],
  selector: 'mode-create-dataset-modal',
  templateUrl: './create-dataset-modal.component.html',
  styleUrls: ['./create-dataset-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateDatasetModalComponent implements OnInit {
  isDatasetLimitExceeded = false;
  form: FormGroup;
  existedNames: string[] = [];
  get datasetName() {
    return this.form.get('datasetName');
  }

  constructor(
    private fb: NonNullableFormBuilder,
    private dialogService: DialogService,
    private cellExportFacade: CellExportFacade,
    private dataViewFacade: DataViewFacade,
    private toast: ToastService,
    private analyticsService: AnalyticsService,
    private metaTag: MetaTagsService,

    @Inject(MAT_LEGACY_DIALOG_DATA)
    private data: {
      cellToken: string;
      cellContentType: ContentType;
    }
  ) {
    this.form = this.fb.group({
      datasetName: ['', [Validators.required, validNameValidator]],
    });
  }

  ngOnInit(): void {
    this.dataViewFacade.getDatasetsLimitExceeded$.subscribe((isLimitExceeded) => {
      this.isDatasetLimitExceeded = isLimitExceeded;
    });

    this.dataViewFacade.enhancedDatasetResults$.subscribe((results) => {
      this.existedNames = results.map((dataset) => dataset.displayName);
      this.updateValidators();
    });
  }
  onSubmit(): void {
    if (this.isDatasetLimitExceeded) {
      this.showDatasetLimitExceededError();
      this.dialogService.closeAll();
      return;
    }
    if (this.datasetName) {
      this.datasetName.setValue(this.datasetName.value?.trim(), { emitEvent: false });
    }
    if (this.form.valid) {
      const { datasetName } = this.form.value;
      const { cellToken, cellContentType } = this.data;
      this.dialogService.closeAll();
      const eventData = {
        context: this.analyticsService.context,
        location: `${AnalyticsEvent.SubContext.ExportDatasetCreateDatasetClicked} Model Create CTA`,
        action: `${AnalyticsEvent.Name.ExportDatasetOutput} - ${AnalyticsEvent.Name.ExportDatasetCreateDataset}`,
        name: this.metaTag.getTag(MetaTags.CurrentUsername),
      };
      this.analyticsService.track(AnalyticsEvent.Name.ExportDataset, eventData);
      this.cellExportFacade.exportCell(cellToken, cellContentType, datasetName);
    }
  }
  close(): void {
    this.dialogService.closeAll();
  }

  private updateValidators(): void {
    const datasetNameControl = this.datasetName;
    datasetNameControl?.setValidators([Validators.required, validNameValidator, duplicateValidator(this.existedNames)]);
    datasetNameControl?.updateValueAndValidity();
  }

  showDatasetLimitExceededError(): void {
    this.toast.error('', {
      closeButton: true,
      disableTimeout: false,
      tapToDismiss: false,
      toastComponent: ExportDatasetLimitExceedComponent,
    });
  }
}
